import {
    baueLampenReports,
    findeFelder,
    hookAktion,
    JABRA_VENDOR_ID,
    leseFeld,
    muteAktion,
    TELEPHONY_PAGE,
    zuSendendeLampen,
    type GespraechsLage,
    type HeadsetAktion,
    type HeadsetFelder,
    type HidCollectionInfo,
    type LampenZustand,
} from "./headsetHid";

/**
 * Die Verbindung zum Headset über `navigator.hid` (Edge/Chrome). Die Regeln
 * stehen in `headsetHid.ts`; hier nur Öffnen, Lesen, Schreiben und das An-
 * und Abstecken. Ein Gerät je Browser-Tab — das erste, das die Telefonie-
 * Tasten hat.
 *
 * Freigabe: `navigator.hid.requestDevice` braucht einen Klick des Nutzers
 * (Knopf „Headset verbinden" in der Schnellwahl). Danach merkt sich der
 * Browser die Freigabe; beim nächsten Laden öffnet `verbindeBekanntes` das
 * Gerät ohne Rückfrage.
 */

interface HidDeviceLike {
    productName: string;
    vendorId: number;
    opened: boolean;
    collections: HidCollectionInfo[];
    open(): Promise<void>;
    close(): Promise<void>;
    sendReport(reportId: number, data: Uint8Array): Promise<void>;
    addEventListener(type: "inputreport", listener: (e: { reportId: number; data: DataView }) => void): void;
    removeEventListener(type: "inputreport", listener: (e: { reportId: number; data: DataView }) => void): void;
}
interface HidLike {
    getDevices(): Promise<HidDeviceLike[]>;
    requestDevice(opts: { filters: Array<{ vendorId?: number; usagePage?: number }> }): Promise<HidDeviceLike[]>;
    addEventListener(type: "connect" | "disconnect", listener: (e: { device: HidDeviceLike }) => void): void;
}

export interface HeadsetStatus {
    /** Kann dieser Browser überhaupt Headsets steuern (WebHID)? */
    unterstuetzt: boolean;
    /** Name des verbundenen Geräts; null = keins. */
    geraet: string | null;
    fehler: string | null;
    /**
     * Darf KMVS das Headset benutzen (Tasten, Lampen)? Aus für alle, die mit
     * der Linkus-App telefonieren — sonst stören sich beide (T-277).
     */
    aktiviert: boolean;
}

export interface HeadsetHandler {
    /** Wie steht das Gespräch gerade? */
    lage: () => GespraechsLage;
    aktion: (a: HeadsetAktion) => void;
}

function hid(): HidLike | null {
    const n = typeof navigator !== "undefined" ? (navigator as unknown as { hid?: HidLike }).hid : undefined;
    return n ?? null;
}

const SCHALTER = "kmvs.telefon.headsetTasten";

function leseAktiviert(): boolean {
    try {
        return window.localStorage.getItem(SCHALTER) !== "aus";
    } catch {
        return true;
    }
}

const hatTelefonie = (d: HidDeviceLike) => {
    const f = findeFelder(d.collections);
    return !!f.hook;
};

class HeadsetVerbindung {
    private geraet: HidDeviceLike | null = null;
    private felder: HeadsetFelder | null = null;
    private handler: HeadsetHandler | null = null;
    private vorher = { hook: 0, mute: 0 };
    /** Zuletzt umgesetzter KMVS-Zustand der Lampen; null = seit dem Verbinden noch keiner. */
    private letzteLampen: LampenZustand | null = null;
    private status: HeadsetStatus = {
        unterstuetzt: !!hid(),
        geraet: null,
        fehler: null,
        aktiviert: typeof window === "undefined" ? true : leseAktiviert(),
    };
    private readonly zuhoerer = new Set<() => void>();
    private ereignisseAngemeldet = false;

    getStatus(): HeadsetStatus {
        return this.status;
    }

    abonnieren(fn: () => void): () => void {
        this.zuhoerer.add(fn);
        return () => this.zuhoerer.delete(fn);
    }

    setHandler(h: HeadsetHandler | null): void {
        this.handler = h;
    }

    private setzeStatus(teil: Partial<HeadsetStatus>): void {
        this.status = { ...this.status, ...teil };
        this.zuhoerer.forEach((fn) => fn());
    }

    /** Beim Laden: ein schon freigegebenes Headset ohne Rückfrage öffnen. */
    async verbindeBekanntes(): Promise<void> {
        const h = hid();
        if (!h || !this.status.aktiviert) return;
        this.anmelden(h);
        if (this.geraet) return;
        try {
            const geraete = await h.getDevices();
            const g = geraete.find(hatTelefonie);
            if (g) await this.oeffne(g);
        } catch (err) {
            console.warn("[telephony] Headset: bekannte Geräte nicht lesbar", err);
        }
    }

    /** Knopf „Headset verbinden" — MUSS aus einem Klick heraus kommen. */
    async verbindeNeu(): Promise<void> {
        const h = hid();
        if (!h) {
            this.setzeStatus({ fehler: "Dieser Browser kann keine Headsets steuern. Bitte Microsoft Edge oder Chrome nutzen." });
            return;
        }
        this.anmelden(h);
        this.setzeAktiviert(true);
        try {
            const auswahl = await h.requestDevice({
                filters: [{ vendorId: JABRA_VENDOR_ID, usagePage: TELEPHONY_PAGE }, { usagePage: TELEPHONY_PAGE }],
            });
            const g = auswahl.find(hatTelefonie) ?? null;
            if (!g) {
                if (auswahl.length > 0) {
                    this.setzeStatus({ fehler: "Dieses Gerät hat keine Telefonie-Tasten (Annehmen/Auflegen)." });
                }
                return;
            }
            await this.oeffne(g);
        } catch (err) {
            console.warn("[telephony] Headset verbinden fehlgeschlagen", err);
            this.setzeStatus({ fehler: "Das Headset konnte nicht verbunden werden." });
        }
    }

    private anmelden(h: HidLike): void {
        if (this.ereignisseAngemeldet) return;
        this.ereignisseAngemeldet = true;
        h.addEventListener("connect", (e) => {
            // Wieder eingesteckt: nur ein schon freigegebenes Gerät kommt hier an.
            if (this.status.aktiviert && !this.geraet && hatTelefonie(e.device)) void this.oeffne(e.device);
        });
        h.addEventListener("disconnect", (e) => {
            if (e.device === this.geraet) {
                this.trennen();
                console.info("[telephony] Headset abgesteckt");
            }
        });
    }

    private readonly aufReport = (e: { reportId: number; data: DataView }) => {
        const f = this.felder;
        const h = this.handler;
        if (!f || !h) return;
        if (f.hook && e.reportId === f.hook.reportId) {
            const v = leseFeld(e.data, f.hook);
            const a = hookAktion(this.vorher.hook, v, f.hook.absolut, h.lage());
            this.vorher.hook = v;
            if (a) {
                console.info(`[telephony] Headset: ${a}`);
                h.aktion(a);
            }
        }
        if (f.mute && e.reportId === f.mute.reportId) {
            const v = leseFeld(e.data, f.mute);
            const a = muteAktion(this.vorher.mute, v, h.lage());
            this.vorher.mute = v;
            if (a) h.aktion(a);
        }
    };

    private async oeffne(g: HidDeviceLike): Promise<void> {
        try {
            if (!g.opened) await g.open();
        } catch (err) {
            // Häufigste Ursache: ein anderes Programm (Linkus, Jabra Direct) hält das Gerät.
            console.warn("[telephony] Headset öffnen fehlgeschlagen", err);
            this.setzeStatus({
                fehler: "Das Headset ist von einem anderen Programm belegt. Bitte die Linkus-App schließen.",
            });
            return;
        }
        this.trennen();
        this.geraet = g;
        this.felder = findeFelder(g.collections);
        this.vorher = { hook: 0, mute: 0 };
        this.letzteLampen = null;
        g.addEventListener("inputreport", this.aufReport);
        console.info("[telephony] Headset verbunden", { name: g.productName, felder: this.felder });
        this.setzeStatus({ geraet: g.productName || "Headset", fehler: null });
    }

    private trennen(): void {
        if (this.geraet) this.geraet.removeEventListener("inputreport", this.aufReport);
        this.geraet = null;
        this.felder = null;
        this.setzeStatus({ geraet: null });
    }

    /**
     * Schalter „Headset-Tasten in KMVS benutzen". Aus: Gerät loslassen, damit
     * die Linkus-App es allein steuert; an: wieder verbinden.
     */
    setzeAktiviert(an: boolean): void {
        try {
            if (an) window.localStorage.removeItem(SCHALTER);
            else window.localStorage.setItem(SCHALTER, "aus");
        } catch {
            /* privater Modus — gilt bis zum Neuladen */
        }
        if (an === this.status.aktiviert) return;
        this.setzeStatus({ aktiviert: an, fehler: null });
        if (an) {
            void this.verbindeBekanntes();
            return;
        }
        const g = this.geraet;
        this.trennen();
        void g?.close().catch(() => {
            /* schon zu */
        });
        console.info("[telephony] Headset-Tasten in KMVS ausgeschaltet");
    }

    /** Lampen nachziehen — nur wenn sich etwas ändert, s. `zuSendendeLampen`. */
    setzeLampen(z: LampenZustand): void {
        const g = this.geraet;
        const f = this.felder;
        if (!g || !f) return;
        const senden = zuSendendeLampen(z, this.letzteLampen, f.hook?.absolut ? this.vorher.hook : 0);
        const eigenAbgehoben = !!this.letzteLampen?.abgehoben;
        this.letzteLampen = z;
        if (!senden) return;
        // Der Hook-Stand folgt dem Gespräch: wird es in KMVS beendet, gilt das
        // Headset als aufgelegt — sonst wäre der nächste Abheben-Druck ein 1→1.
        if (eigenAbgehoben && !z.abgehoben && f.hook?.absolut) this.vorher.hook = 0;
        for (const { reportId, data } of baueLampenReports(f, senden)) {
            void g.sendReport(reportId, data).catch((err) => {
                console.warn("[telephony] Headset-Lampen setzen fehlgeschlagen", err);
            });
        }
    }
}

export const headset = new HeadsetVerbindung();
